import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { RowSelectionState, DropdownItem, Multiselect, M, Button } from 'design-system-web';
import { bulkAssignTeamsMutation } from 'graphql-queries';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { Assignable } from 'domains-work-order-type-contracts';

/** @deprecated Use Assignable instead */
type AdminUser = Assignable;

const SERVICE_REQUEST_SELECTION_LIMIT = 100;

type Props = {
  rowSelection?: RowSelectionState;
  assignableTeams: AdminUser[];
  refetch?: () => void;
};

export function BulkTeamsAssignAction({
  rowSelection,
  assignableTeams,
  refetch,
}: Props) {
  const { t } = useTranslation();
  const [bulkAssignTeams] = useMutation(bulkAssignTeamsMutation);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);

  const selectedWorkOrderIds = useMemo(() => {
    if (!rowSelection) {
      return [];
    }

    return Object.entries(rowSelection)
      .filter(([, isSelected]) => isSelected)
      .map(([workOrderId]) => workOrderId);
  }, [rowSelection]);

  const openModal = async () => {
    if (selectedWorkOrderIds.length === 0) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.noSelection')
      );

      return;
    }

    if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.limitExceeded', {
          serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
          selectedWorkOrderCount: selectedWorkOrderIds.length,
        }),
        5000
      );

      return;
    }

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTeams([]);
  };

  const handleConfirm = async () => {
    if (selectedTeams.length === 0) {
      return;
    }

    if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.limitExceeded', {
          serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
          selectedWorkOrderCount: selectedWorkOrderIds.length,
        }),
        5000
      );

      closeModal();
      return;
    }

    setLoading(true);

    try {
      const assigneeGroups = selectedTeams.map(team => team.value);

      const result = await bulkAssignTeams({
        variables: {
          serviceRequestIds: selectedWorkOrderIds,
          assigneeGroups,
        },
      });

      const { successCount, failureCount } = result.data?.bulkAssignTeams || {
        successCount: 0,
        failureCount: selectedWorkOrderIds.length,
      };

      if (failureCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignTeams.success', {
            count: successCount,
          })
        );
      } else if (successCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignTeams.error')
        );
      } else {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignTeams.partial', {
            successCount,
            failureCount,
          }),
          5000
        );
      }

      if (refetch) {
        refetch();
      }
    } catch {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.error')
      );
    }

    setLoading(false);
    closeModal();
  };

  return (
    <>
      <Button
        variant="secondary"
        size="large"
        onClick={openModal}
        testId="bulk-sr-teams-assign-select"
      >
        {t('web.admin.serviceRequest.bulkActions.assignTeams.selectTeams')}
      </Button>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        handleConfirm={handleConfirm}
        title={t('web.admin.serviceRequest.bulkActions.assignTeams.confirm.title')}
        confirmText={t(
          'web.admin.serviceRequest.bulkActions.assignTeams.confirm.button'
        )}
        cancelText={t(
          'web.admin.serviceRequest.bulkActions.assignTeams.cancel.button'
        )}
        loading={loading}
        confirmActionDisabled={selectedTeams.length === 0 || loading}
        cancelActionDisabled={loading}
        isFullWidth
      >
        <M>
          {t('web.admin.serviceRequest.bulkActions.assignTeams.confirm.message', {
            count: selectedWorkOrderIds.length,
          })}
        </M>
        <Multiselect
          items={assignableTeams ?? []}
          value={selectedTeams.map(team => ({ value: team.value }))}
          onChange={(items: DropdownItem<string>[]) => {
            const matchedTeams = items
              .map(item =>
                (assignableTeams ?? []).find(team => team.value === item.value)
              )
              .filter((team): team is AdminUser => Boolean(team));

            setSelectedTeams(matchedTeams);
          }}
          placeholder={t(
            'web.admin.serviceRequest.bulkActions.assignTeams.selectTeams'
          )}
          doTranslation={false}
          isFullWidth
        />
      </ConfirmationModal>
    </>
  );
}
